/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/CustomerCompanyUsersTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Switch } from "@mui/material";
import { ReactComponent as EditIcon } from "../../assets/icons/CustomerCompanyUsersTable/EditIcon.svg";
import { ReactComponent as ActiveUserIcon } from "../../assets/icons/CustomerCompanyUsersTable/ActiveUserIcon.svg";
import { ReactComponent as InactiveUserIcon } from "../../assets/icons/CustomerCompanyUsersTable/InactiveUserIcon.svg";
import styles from "./CustomerCompanyUsersTable.module.css";
import { getCustomerCompanyUsersByCompanyId } from "../../logic/customerCompanyUser";

/** API path for customer company user status */
const CUSTOMER_USER_STATUS_PATH = "customerCompanyUser";

/**
 * Renders a single user row in the customer company users table.
 * @param {Object} props
 * @param {Object} props.user - User data
 * @param {function} props.onEdit - Edit callback
 * @param {function} props.onStatusChange - Status change callback
 * @param {function} props.t - Translation function
 * @returns {JSX.Element}
 */
function UserRow({ user, onEdit, onStatusChange, t }) {
  return (
    <tr key={user.id}>
      <td>
        <div className={styles.userStateCell}>
          {user.compradorActivo ? (
            <ActiveUserIcon className={styles.userStateIcon} />
          ) : (
            <InactiveUserIcon className={styles.userStateIcon} />
          )}
          <span>
            {user.compradorActivo
              ? t("components.customerCompanyUsersTable.activeUser")
              : t("components.customerCompanyUsersTable.inactiveUser")}
          </span>
        </div>
      </td>
      <td>{user.nombre}</td>
      <td>{user.correoElectronico}</td>
      <td>
        <Switch
          checked={user.compradorActivo}
          onChange={() => onStatusChange(user)}
        />
      </td>
      <td className={styles.actionsCell}>
        <button onClick={() => onEdit(user)} className={styles.actionButton}>
          <EditIcon />
        </button>
      </td>
    </tr>
  );
}

/**
 * Table of customer company users for a company. Supports edit and enable/disable.
 * @param {Object} props
 * @param {string} props.companyId - Company id to load users for
 * @param {boolean} props.isNewCompany - Whether the company is new (no users yet)
 * @param {function} [props.onEditUser] - Callback when user edit is clicked
 * @param {function} props.navigate - React Router navigate
 * @param {*} props.refreshTrigger - Dependency to refetch when changed
 * @returns {JSX.Element}
 */
const CustomerCompanyUsersTable = ({
  companyId,
  isNewCompany,
  onEditUser,
  navigate,
  refreshTrigger,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCustomerCompanyUsersByCompanyId(
        companyId,
        navigate,
      );
      setUsers(Array.isArray(result) ? result : []);
    } catch (err) {
      logger.error("Error in fetchUsers:", { err: sanitizeError(err) });
      setError(err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    if (!isNewCompany && companyId) {
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [companyId, isNewCompany, refreshTrigger, fetchUsers]);

  const handleEdit = useCallback(
    (user) => {
      onEditUser?.(user);
    },
    [onEditUser],
  );

  const handleStatusChange = useCallback(async (user) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}${CUSTOMER_USER_STATUS_PATH}/${user._id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ activo: !user.compradorActivo }),
        },
      );
      if (response.ok) {
        setUsers((prev) =>
          prev.map((u) =>
            u._id === user._id
              ? { ...u, compradorActivo: !u.compradorActivo }
              : u,
          ),
        );
      } else {
        logger.error("Failed to update user status");
      }
    } catch (err) {
      logger.error("Error in handleStatusChange:", { err: sanitizeError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }
  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t("components.customerCompanyUsersTable.userState")}</th>
            <th>{t("components.customerCompanyUsersTable.userName")}</th>
            <th>{t("components.customerCompanyUsersTable.userEmail")}</th>
            <th>
              {t("components.customerCompanyUsersTable.enableDisableUser")}
            </th>
            <th className={styles.actionsHeader}>
              {t("components.customerCompanyUsersTable.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <UserRow
              key={user.id}
              user={user}
              onEdit={handleEdit}
              onStatusChange={handleStatusChange}
              t={t}
            />
          ))}
          {users.length === 0 && (
            <tr>
              <td colSpan={5} className={styles.noUsersCell}>
                {t("components.customerCompanyUsersTable.noUsers")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerCompanyUsersTable;
